import { Box, Typography } from "@mui/material";
import BuildCircleRoundedIcon from "@mui/icons-material/BuildCircleRounded";
import BuildCircleOutlinedIcon from "@mui/icons-material/BuildCircleOutlined";
import { NavLink, useMatch, useNavigate } from "react-router-dom";
import {
  AdminIcon,
  AdminIconActive,
  AllStudiesIcon,
  AllStudiesIconActive,
  DashboardIcon,
  DashboardIconActive,
  Logo,
  SmallLogo,
  RegistryIcon,
  RegistryIconActive,
  StudySettingsIcon,
  StudySettingsIconActive,
} from "../Common/assets/Sidebar";
import VersionWrapper from "../Common/UI/VersionWrapper";
import { useAppSelector } from "../../Redux/hooks";
import { platformAdminPermissions } from "../../utils/roles";
import { useMemo, useState } from "react";
import SidebarToggle from "./SidebarToggle";
// import UserProfileCard from "./UserProfileCard";
import ExpandableMenu from "./ExpandableMenu";
// import { setCollapsed } from "../../Redux/reducers/appSlice";

const Sidebar = () => {
  const navigate = useNavigate();
  const { platformPermissions } = useAppSelector((state) => state.user);
  const collapsed = useAppSelector((state) => state.app.collapsed);
  const [openMenu, setOpenMenu] = useState<string | null>(null);

  function handleMenuToggle(menuKey: string, defaultRoute: string) {
    navigate(defaultRoute);
    if (collapsed) {
      setOpenMenu(menuKey);
    } else {
      setOpenMenu((prev) => (prev === menuKey ? null : menuKey));
    }
  }

  const matchFunctions: {
    [key: string]: boolean;
  } = {
    designer: !!useMatch("/app/designer/*"),
    consent: !!useMatch("/app/consent/*"),
    tracking: !!useMatch("/app/tracker/*"),
    participants: !!useMatch("/app/participants/*"),
    administration: !!useMatch("/app/platform-users/*"),
    appointments: !!useMatch("/app/appointments/*"),
    settings: !!useMatch("/app/settings/*"),

    dashboard: !!useMatch("/app/dashboard/*"),
    myStudies: !!useMatch("/app/studies/*"),
    allStudies: !!useMatch("/app/all-studies/*"),
    features: !!useMatch("/app/features/*"),
    platformUsers: !!useMatch("/app/platform-users/*"),
    templates: !!useMatch("/app/templates/*"),
    location: !!useMatch("/app/location/*"),
    globalTags: !!useMatch("/app/global-tags/*"),
    canonicalQuestions: !!useMatch("/app/canonical-questions/*"),
    emailTemplates: !!useMatch("/app/email-templates/*"),
    auditTrail: !!useMatch("/app/audit-trail/*"),
    sponsors: !!useMatch("/app/sponsors/*"),
    maintenanceSchedule: !!useMatch("/app/maintenance-schedule/*"),
  };

  const rbac = useMemo(
    () => ({
      view: platformPermissions.includes(platformAdminPermissions.viewPlatform),
      managePlatform: platformPermissions.includes(
        platformAdminPermissions.managePlatform,
      ),
      superManage: platformPermissions.includes(
        platformAdminPermissions.superManage,
      ),
      createStudy: platformPermissions.includes(
        platformAdminPermissions.createStudy,
      ),
      manageUser: platformPermissions.includes(
        platformAdminPermissions.manageUser,
      ),
      manageRegistry: platformPermissions.includes(
        platformAdminPermissions.manageRegistry,
      ),
    }),
    [platformPermissions],
  );

  const registryNavLinks = useMemo(
    () =>
      [
        {
          label: "Designer",
          matchKey: "designer",
          link: "/app/designer",
          enabled: rbac.managePlatform,
        },
        // {
        //   label: "Consent",
        //   matchKey: "consent",
        //   link: "/app/consent",
        //   enabled: rbac.managePlatform,
        // },
        // {
        //   label: "Tracker",
        //   matchKey: "tracking",
        //   link: "/app/tracker",
        //   enabled: rbac.managePlatform,
        // },
        {
          label: "Participants",
          matchKey: "participants",
          link: "/app/participants",
          enabled: rbac.managePlatform,
        },
        {
          label: "Appointments",
          matchKey: "appointments",
          link: "/app/appointments",
          enabled: rbac.managePlatform,
        },
        {
          label: "Settings",
          matchKey: "settings",
          link: "/app/settings",
          enabled: rbac.managePlatform,
        },
      ].filter((item) => item.enabled),
    [rbac.managePlatform],
  );

  const studiesNavLinks = useMemo(
    () =>
      [
        {
          label: "My Studies",
          matchKey: "myStudies",
          link: "/app/studies",
          enabled: true,
        },
        {
          label: "All Studies",
          matchKey: "allStudies",
          link: "/app/all-studies",
          enabled: rbac.createStudy,
        },
      ].filter((item) => item.enabled),
    [rbac.createStudy],
  );

  const manageNavLinks = useMemo(
    () =>
      [
        {
          label: "Features",
          matchKey: "features",
          link: "/app/features",
          enabled: rbac.managePlatform,
        },
        {
          label: "Templates",
          matchKey: "templates",
          link: "/app/templates",
          enabled: true,
        },
        {
          label: "Audit Trail",
          matchKey: "auditTrail",
          link: "/app/audit-trail",
          enabled: true,
        },
        {
          label: "Tags",
          matchKey: "globalTags",
          link: "/app/global-tags",
          enabled: true,
        },
        {
          label: "Canonical Questions",
          matchKey: "canonicalQuestions",
          link: "/app/canonical-questions",
          enabled: true,
        },
        {
          label: "Email Template",
          matchKey: "emailTemplates",
          link: "/app/email-templates",
          enabled: true,
        },
        {
          label: "Location & Rooms",
          matchKey: "location",
          link: "/app/location",
          enabled: rbac.manageRegistry,
        },
        {
          label: "Clients/Sponsors",
          matchKey: "sponsors",
          link: "/app/sponsors",
          enabled: rbac.manageRegistry,
        },
      ].filter((item) => item.enabled),
    [rbac.managePlatform, rbac.manageRegistry],
  );

  return (
    <Box
      className={`sidebar ${collapsed ? "collapsed" : ""}`}
      sx={{
        // "& .active.sider-menu": {
        //   backgroundColor: "primary.main",
        // },
        "& .sider-submenu": {
          color: "text.secondary",
        },
      }}
    >
      <SidebarToggle type="main" />
      <Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            paddingBottom: 2,
            margin: "10px 0px",
          }}
        >
          {!collapsed ? <Logo /> : <SmallLogo />}
        </Box>
        {/* <Box
          sx={{
            display: "flex",
            alignItems: "center",
            border: "1px solid #1F438A",
            borderRadius: "10px",
            height: "41px",
            p: 1,
            mb: 1.5,
            width: "100%",
          }}
        >
          <UserProfileCard collapsed={collapsed} />
        </Box> */}

        {/* Registry Section */}
        {rbac.managePlatform && registryNavLinks.length > 0 && (
          <ExpandableMenu
            menuKey="registry"
            title="Database"
            icon={<RegistryIcon />}
            activeIcon={<RegistryIconActive />}
            navLinks={registryNavLinks}
            matchFunctions={matchFunctions}
            openMenu={openMenu}
            collapsed={collapsed}
            onMenuToggle={handleMenuToggle}
          />
        )}
        {studiesNavLinks.length > 0 && (
          <ExpandableMenu
            menuKey="studies"
            title="Studies"
            icon={<AllStudiesIcon />}
            activeIcon={<AllStudiesIconActive />}
            navLinks={studiesNavLinks}
            matchFunctions={matchFunctions}
            openMenu={openMenu}
            collapsed={collapsed}
            onMenuToggle={handleMenuToggle}
          />
        )}

        {/* Manage Section */}
        {rbac.managePlatform && manageNavLinks.length > 0 && (
          <ExpandableMenu
            menuKey="manage"
            title="Manage"
            icon={<StudySettingsIcon />}
            activeIcon={<StudySettingsIconActive />}
            navLinks={manageNavLinks}
            matchFunctions={matchFunctions}
            openMenu={openMenu}
            collapsed={collapsed}
            onMenuToggle={handleMenuToggle}
          />
        )}

        {rbac.manageUser && (
          <NavLink
            to={`/app/platform-users`}
            title="Administration"
            className={({ isActive }) =>
              isActive ? "sider-menu active" : "sider-menu"
            }
          >
            {matchFunctions.administration ? (
              <AdminIconActive />
            ) : (
              <AdminIcon />
            )}
            {!collapsed && (
              <Typography variant="body2" fontWeight="regular">
                Administration
              </Typography>
            )}
          </NavLink>
        )}

        {rbac.superManage && (
          <NavLink
            to={`/app/maintenance-schedule`}
            title="Maintenance"
            className={({ isActive }) =>
              isActive ? "sider-menu active" : "sider-menu"
            }
          >
            {matchFunctions.maintenanceSchedule ? (
              <BuildCircleRoundedIcon
                sx={{ color: (theme) => theme.palette.primary.main }}
              />
            ) : (
              <BuildCircleOutlinedIcon
                sx={{ color: (theme) => theme.palette.text.secondary }}
              />
            )}
            {!collapsed && (
              <Typography variant="body2" fontWeight="regular">
                Maintenance
              </Typography>
            )}
          </NavLink>
        )}

        {rbac.manageRegistry && (
          <NavLink
            to={`/app/dashboard`}
            title="Dashboard"
            className={({ isActive }) =>
              isActive ? "sider-menu active" : "sider-menu"
            }
          >
            {matchFunctions.dashboard ? (
              <DashboardIconActive />
            ) : (
              <DashboardIcon />
            )}
            {!collapsed && (
              <Typography variant="body2" fontWeight="regular">
                Dashboard
              </Typography>
            )}
          </NavLink>
        )}
      </Box>
      <VersionWrapper />
    </Box>
  );
};

export default Sidebar;
